import React from 'react'
import { VisuallyHidden } from '@overdose/components'
import { IconClockHour5, IconInfoCircle } from '@tabler/icons-react'
import classNames from 'classnames'
import { Button, Link } from '~/components'
import { Image } from '~/components/Image'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrc } from '~/helpers'
import { usePage } from '~/hooks'
import styles from './StoreInfo.module.css'

export const StoreInfo = ({ store }) => {
  const page = usePage()
  const isRTM = page?.brand === 'rtm'

  const {
    address,
    openingHours,
    storeLocatorFeature,
    phoneNumber,
    email,
    specialTradingHours,
    shopImage,
  } = store
  const { city, streetNumber, streetName, state, postalCode, country } = address
  const colItemSpan = shopImage ? 'lg:col-span-4' : 'md:col-span-4'
  const customVariant = isRTM
    ? TypographyVariant.Heading6
    : TypographyVariant.BodyLargeMedium
  return (
    <div
      className={`grid gap-2 md:gap-4 ${
        shopImage ? 'grid-cols-12 mb-12 md:mb-6' : 'grid-cols-8'
      } xl:col-span-8 lg:col-span-10 col-span-full xl:col-start-3 lg:col-start-2 text-grey-900 mt-2 md:mt-4 mb-6 md:mb-10`}>
      <div
        className={classNames(
          'col-span-full flex flex-col md:gap-6 gap-2',
          colItemSpan
        )}>
        <div>
          <Typography
            variant={customVariant}
            className='py-4 border-b text-primary'
            tag={TypographyTag.p}>
            Address
          </Typography>
          <address className='mt-4'>
            {[
              city,
              `${streetNumber} ${streetName}`,
              `${city}, ${state} ${postalCode}`,
              country,
            ].map((field) => {
              return (
                <Typography
                  key={field}
                  variant={TypographyVariant.BodyRegularExtraLineHeight}
                  className='py-1 text-secondary-muted'
                  tag={TypographyTag.div}>
                  {field}
                </Typography>
              )
            })}
          </address>
        </div>
        {phoneNumber && (
          <div>
            <Typography
              variant={customVariant}
              className='py-4 border-b text-primary'
              tag={TypographyTag.div}>
              Phone
            </Typography>

            <div className='mt-4'>
              <Link href={`tel:${phoneNumber.replace(/\s/g, '')}`}>
                <Typography
                  variant={TypographyVariant.BodyRegularExtraLineHeight}
                  tag={TypographyTag.p}
                  className='!underline text-primary'>
                  {phoneNumber}
                </Typography>
              </Link>
            </div>
          </div>
        )}
        {email && (
          <div>
            <Typography
              variant={customVariant}
              className='py-4 border-b text-primary'
              tag={TypographyTag.div}>
              Email
            </Typography>
            <div className='mt-4'>
              <a href={`mailto:${email}`}>
                <Typography
                  variant={TypographyVariant.BodyRegularExtraLineHeight}
                  className='!underline text-primary'
                  tag={TypographyTag.div}>
                  {email}
                </Typography>
              </a>
            </div>
          </div>
        )}
      </div>
      <div
        className={classNames(
          'col-span-full flex flex-col md:gap-6 gap-2',
          colItemSpan
        )}>
        {openingHours?.length > 0 && (
          <div>
            <Typography
              variant={customVariant}
              className='py-4 border-b text-primary'
              tag={TypographyTag.div}>
              Opening Hours
            </Typography>
            <div className='mt-4 mb-2 w-full'>
              <ul className='md:w-2/3 flex flex-col gap-2'>
                {openingHours.map((hour) => {
                  return (
                    <li
                      key={hour.label}
                      className='flex justify-between text-secondary-muted'>
                      <Typography
                        variant={TypographyVariant.BodyRegularBold}
                        tag={TypographyTag.span}
                        className='capitalize'>
                        {hour.label.toLowerCase()}
                      </Typography>
                      <div className='flex flex-col gap-1'>
                        <Typography
                          variant={TypographyVariant.BodyRegular}
                          tag={TypographyTag.span}>
                          {hour.timings.start} - {hour.timings.finish}
                        </Typography>
                        {hour.reason && (
                          <div className='flex gap-2 mb-1 items-center'>
                            <Button
                              title='Info icon'
                              icon={<IconInfoCircle color='#00aeef' />}
                              iconOnly
                              variant='ghost'
                              size='xs'
                              style={{ '--btn-size-xs-height': '16px' }}>
                              <VisuallyHidden>Info icon</VisuallyHidden>
                            </Button>
                            <Typography
                              variant={TypographyVariant.BodyRegular}
                              tag={TypographyTag.span}>
                              {hour.reason}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )}
        {!!specialTradingHours?.length && (
          <div
            className={classNames(
              'flex gap-2',
              styles.specialHours,
              isRTM && 'md:w-2/3 lg:w-full'
            )}>
            <Button
              title='Special Opening Times'
              icon={<IconClockHour5 color='#0090D4' />}
              iconOnly
              className={classNames('hidden', styles.rtmBtn)}
              style={{ '--btn-icon-size': '24px' }}
              variant='ghost'
              size='xs'>
              <VisuallyHidden>Special Opening Times</VisuallyHidden>
            </Button>
            <div className='w-full'>
              <Typography
                variant={
                  isRTM
                    ? TypographyVariant.BodyRegularBold
                    : TypographyVariant.BodyLargeMedium
                }
                className={classNames(
                  'border-b py-4',
                  styles.specialHoursTitle
                )}
                tag={TypographyTag.div}>
                Special Opening Times
              </Typography>
              <div
                className={classNames(
                  'mt-4 w-full',
                  styles.specialHourWrapper
                )}>
                <ul className='flex flex-col gap-2'>
                  {specialTradingHours.map((specialHour) => {
                    const { label, date, closed, timings, message } =
                      specialHour
                    return (
                      <li key={specialHour.label} className='flex gap-4'>
                        <div
                          className={classNames(
                            'flex justify-between w-2/3',
                            styles.specialHour
                          )}>
                          <Typography
                            variant={TypographyVariant.BodyRegularBold}
                            tag={TypographyTag.span}>
                            {label} {date}
                          </Typography>
                          {closed ? (
                            <Typography
                              variant={TypographyVariant.BodyRegular}
                              tag={TypographyTag.span}>
                              Closed
                            </Typography>
                          ) : (
                            <Typography
                              variant={TypographyVariant.BodyRegular}
                              tag={TypographyTag.span}>
                              {`${timings.start} - ${timings.finish}`}
                            </Typography>
                          )}
                        </div>
                        {message && (
                          <Typography
                            variant={TypographyVariant.BodyRegular}
                            tag={TypographyTag.span}>
                            {message}
                          </Typography>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}
        {storeLocatorFeature && (
          <div>
            <Typography
              variant={customVariant}
              className='py-4 border-b mt-6 md:mt-0 text-primary'
              tag={TypographyTag.div}>
              Features
            </Typography>
            <ul className='list-disc flex flex-col mt-4 list-inside pl-4'>
              {storeLocatorFeature.map((storeLocatorField) => {
                return (
                  <li key={storeLocatorField}>
                    <Typography
                      variant={TypographyVariant.BodyRegularExtraLineHeight}
                      tag={TypographyTag.span}
                      className='text-secondary-muted'>
                      {storeLocatorField}
                    </Typography>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
      {shopImage?.src && (
        <div className={classNames('col-span-full', colItemSpan)}>
          <Image
            src={getImageSrc(shopImage.src, '600')}
            addSrcSet={false}
            width={shopImage.width}
            height={shopImage.height}
            alt={shopImage.altText}
          />
        </div>
      )}
    </div>
  )
}
